import { useLayoutEffect } from 'react'

import * as Sentry from '@sentry/react'

import { useSelector, history } from '@tabeeb/store'
import { getTenantName } from '@tabeeb/modules/appConfigState/selectors'
import domainService from '@tabeeb/services/domainService'
import { getMe } from '@tabeeb/modules/account/selectors'
import { UserInfoResponse } from '@tabeeb/contracts/main-api/swagger-main-api'

const sentryIntegrations = []

sentryIntegrations.push(
  Sentry.reactRouterV5BrowserTracingIntegration({
    history,
  })
)

sentryIntegrations.push(
  Sentry.replayIntegration({
    maskAllText: false,
    maskAllInputs: false,
  })
)

if (process.env.SENTRY_CONSOLE_LOGGING_ENABLED === 'true') {
  sentryIntegrations.push(
    Sentry.captureConsoleIntegration({
      levels: ['error'],
    })
  )
}

Sentry.init({
  enabled: process.env.SENTRY_ENABLED === 'true',
  dsn: process.env.SENTRY_DSN,
  sendDefaultPii: true,
  environment: process.env.SENTRY_ENVIRONMENT,
  integrations: sentryIntegrations,
  initialScope: {
    level: 'error',
  },
  ignoreErrors: ['ResizeObserver loop limit exceeded', 'Authentication expired', "Status code '401'"],
  replaysSessionSampleRate: Number(process.env.SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
  replaysOnErrorSampleRate: Number(process.env.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE),
})

const SentryContextProvider = ({ children }: { children: React.ReactNode }) => {
  const tenantName = useSelector(getTenantName)

  useLayoutEffect(() => {
    Sentry.setTag('tenant_subdomain', domainService.subdomain)
    Sentry.setContext('tenant', {
      name: tenantName,
      subdomain: domainService.subdomain,
    })
  }, [tenantName])

  const user = useSelector(getMe) as UserInfoResponse | null

  useLayoutEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.Id,
        identityGuid: user.IdentityGuid,
      })
    } else {
      Sentry.setUser(null)
    }
  }, [user])

  return children
}

export default SentryContextProvider
